import { useState, useEffect } from 'react'
import { Shield, Plus, Edit, Trash2, Users, Lock } from 'lucide-react'
import Layout from '../../../components/vision/Layout'
import ProtectedRoute from '../../../components/vision/ProtectedRoute'

interface Perfil {
  id: string
  nome: string
  descricao: string
  cor: string
  icone: string
  is_sistema: boolean
  is_ativo: boolean
  nivel_hierarquia: number
  criado_em: string
}

interface Permissao {
  id: string
  chave: string
  nome: string
  descricao: string
  categoria: string
  recurso: string
}

function PerfisPage() {
  const [perfis, setPerfis] = useState<Perfil[]>([])
  const [permissoesDisponiveis, setPermissoesDisponiveis] = useState<Permissao[]>([])
  const [permissoesSelecionadas, setPermissoesSelecionadas] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingPerfil, setEditingPerfil] = useState<Perfil | null>(null)
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    cor: '#3B82F6',
    icone: 'Shield',
    nivel_hierarquia: 1,
    is_ativo: true
  })

  useEffect(() => {
    fetchPerfis()
    fetchPermissoes()
  }, [])

  const fetchPerfis = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/gestao/perfis')
      if (response.ok) {
        const data = await response.json()
        setPerfis(data)
      }
    } catch (error) {
      console.error('Erro ao buscar perfis:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchPermissoes = async () => {
    try {
      const response = await fetch('/api/gestao/permissoes')
      if (response.ok) {
        const data = await response.json()
        setPermissoesDisponiveis(data)
      }
    } catch (error) {
      console.error('Erro ao buscar permissões:', error)
    }
  }

  const fetchPermissoesPerfil = async (perfilId: string) => {
    try {
      const response = await fetch(`/api/gestao/perfis?id=${perfilId}&includePermissions=true`)
      if (response.ok) {
        const data = await response.json()
        if (data.permissoes) {
          setPermissoesSelecionadas(data.permissoes.map((p: any) => p.id))
        }
      }
    } catch (error) {
      console.error('Erro ao buscar permissões do perfil:', error)
    }
  }

  const handleOpenModal = async (perfil?: Perfil) => {
    if (perfil) {
      setEditingPerfil(perfil)
      setFormData({
        nome: perfil.nome,
        descricao: perfil.descricao,
        cor: perfil.cor,
        icone: perfil.icone,
        nivel_hierarquia: perfil.nivel_hierarquia,
        is_ativo: perfil.is_ativo
      })
      await fetchPermissoesPerfil(perfil.id)
    } else {
      setEditingPerfil(null)
      setFormData({
        nome: '',
        descricao: '',
        cor: '#3B82F6',
        icone: 'Shield',
        nivel_hierarquia: 1,
        is_ativo: true
      })
      setPermissoesSelecionadas([])
    }
    setShowModal(true)
  }

  const togglePermissao = (permissaoId: string) => {
    setPermissoesSelecionadas(prev => {
      if (prev.includes(permissaoId)) {
        return prev.filter(id => id !== permissaoId)
      } else {
        return [...prev, permissaoId]
      }
    })
  }

  const toggleModulo = (recurso: string) => {
    const permissoesDoRecurso = permissoesDisponiveis
      .filter(p => p.recurso === recurso)
      .map(p => p.id)
    
    const todasSelecionadas = permissoesDoRecurso.every(id => 
      permissoesSelecionadas.includes(id)
    )

    if (todasSelecionadas) {
      setPermissoesSelecionadas(prev => 
        prev.filter(id => !permissoesDoRecurso.includes(id))
      )
    } else {
      setPermissoesSelecionadas(prev => [
        ...prev.filter(id => !permissoesDoRecurso.includes(id)),
        ...permissoesDoRecurso
      ])
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingPerfil(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      const url = editingPerfil 
        ? `/api/gestao/perfis?id=${editingPerfil.id}`
        : '/api/gestao/perfis'
      
      const method = editingPerfil ? 'PUT' : 'POST'
      
      const payload = {
        ...formData,
        permissoes: permissoesSelecionadas
      }
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })

      if (response.ok) {
        alert(editingPerfil ? 'Perfil atualizado com sucesso!' : 'Perfil criado com sucesso!')
        handleCloseModal()
        fetchPerfis()
      } else {
        const error = await response.json()
        alert(`Erro: ${error.error || 'Erro ao salvar perfil'}`)
      }
    } catch (error) {
      console.error('Erro ao salvar perfil:', error)
      alert('Erro ao salvar perfil')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este perfil?')) return

    try {
      const response = await fetch(`/api/gestao/perfis?id=${id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        alert('Perfil excluído com sucesso!')
        fetchPerfis()
      } else {
        const error = await response.json()
        alert(`Erro: ${error.error || 'Erro ao excluir perfil'}`)
      }
    } catch (error) {
      console.error('Erro ao excluir perfil:', error)
      alert('Erro ao excluir perfil')
    }
  }

  const getIconComponent = (iconName: string) => {
    const icons: { [key: string]: any } = {
      Shield,
      Users,
      Lock
    }
    const IconComponent = icons[iconName] || Shield
    return IconComponent
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-500 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestão de Perfis</h1>
                <p className="text-sm text-gray-500">Gerencie perfis de acesso e permissões</p>
              </div>
            </div>
            
            <button 
              onClick={() => handleOpenModal()}
              className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              Novo Perfil
            </button>
          </div>
        </div>

        {/* Lista de Perfis */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <p className="mt-2 text-gray-600">Carregando perfis...</p>
            </div>
          ) : perfis.length === 0 ? (
            <div className="col-span-full p-8 text-center">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Nenhum perfil encontrado</p>
            </div>
          ) : (
            perfis.map((perfil) => {
              const IconComponent = getIconComponent(perfil.icone)
              
              return (
                <div
                  key={perfil.id}
                  className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                >
                  {/* Header do Card */}
                  <div 
                    className="p-4"
                    style={{ backgroundColor: `${perfil.cor}15` }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div 
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: perfil.cor }}
                      >
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      
                      {perfil.is_sistema && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
                          Sistema
                        </span>
                      )}
                    </div>
                    
                    <h3 className="text-lg font-bold text-gray-900 mb-1">
                      {perfil.nome}
                    </h3>
                    
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {perfil.descricao}
                    </p>
                  </div>

                  {/* Conteúdo do Card */}
                  <div className="p-4 border-t border-gray-100">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          perfil.is_ativo 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {perfil.is_ativo ? 'Ativo' : 'Inativo'}
                        </span>
                        
                        <span className="text-gray-500">
                          Nível {perfil.nivel_hierarquia}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-end space-x-2">
                    <button
                      onClick={() => handleOpenModal(perfil)}
                      className="inline-flex items-center px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      disabled={perfil.is_sistema}
                      title={perfil.is_sistema ? 'Perfis do sistema não podem ser editados' : 'Editar'}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </button>
                    
                    <button
                      onClick={() => handleDelete(perfil.id)}
                      className="inline-flex items-center px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={perfil.is_sistema}
                      title={perfil.is_sistema ? 'Perfis do sistema não podem ser excluídos' : 'Excluir'}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Excluir
                    </button>
                  </div>
                </div>
              )
            })
          )}
        </div>

        {/* Rodapé com contagem */}
        <div className="mt-6 text-sm text-gray-600 text-center">
          Total de {perfis.length} perfis cadastrados
        </div>

        {/* Modal de Criar/Editar Perfil */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingPerfil ? 'Editar Perfil' : 'Novo Perfil'}
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome do Perfil *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <textarea
                    required
                    rows={3}
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cor *
                    </label>
                    <input
                      type="color"
                      required
                      value={formData.cor}
                      onChange={(e) => setFormData({ ...formData, cor: e.target.value })}
                      className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Ícone *
                    </label>
                    <select
                      required
                      value={formData.icone}
                      onChange={(e) => setFormData({ ...formData, icone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="Shield">Shield</option>
                      <option value="Users">Users</option>
                      <option value="Lock">Lock</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nível Hierárquico *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      max="10"
                      value={formData.nivel_hierarquia}
                      onChange={(e) => setFormData({ ...formData, nivel_hierarquia: parseInt(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status *
                    </label>
                    <select
                      required
                      value={formData.is_ativo ? 'true' : 'false'}
                      onChange={(e) => setFormData({ ...formData, is_ativo: e.target.value === 'true' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="true">Ativo</option>
                      <option value="false">Inativo</option>
                    </select>
                  </div>
                </div>

                {/* Seção de Permissões */}
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    Permissões de Acesso
                  </h3>
                  
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {Object.entries(
                      permissoesDisponiveis.reduce((acc, perm) => {
                        if (!acc[perm.recurso]) {
                          acc[perm.recurso] = []
                        }
                        acc[perm.recurso].push(perm)
                        return acc
                      }, {} as Record<string, Permissao[]>)
                    ).map(([recurso, permissoes]) => {
                      const todasSelecionadas = permissoes.every(p => 
                        permissoesSelecionadas.includes(p.id)
                      )
                      const algumasSelecionadas = permissoes.some(p => 
                        permissoesSelecionadas.includes(p.id)
                      )

                      return (
                        <div key={recurso} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={todasSelecionadas}
                                onChange={() => toggleModulo(recurso)}
                                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                              />
                              <span className="ml-2 font-semibold text-gray-900 capitalize">
                                {recurso}
                              </span>
                            </label>
                            {algumasSelecionadas && (
                              <span className="text-xs text-purple-600 font-medium">
                                {permissoes.filter(p => permissoesSelecionadas.includes(p.id)).length} / {permissoes.length}
                              </span>
                            )}
                          </div>
                          
                          <div className="ml-6 space-y-2">
                            {permissoes.map(permissao => (
                              <label
                                key={permissao.id}
                                className="flex items-start cursor-pointer group"
                              >
                                <input
                                  type="checkbox"
                                  checked={permissoesSelecionadas.includes(permissao.id)}
                                  onChange={() => togglePermissao(permissao.id)}
                                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-0.5"
                                />
                                <div className="ml-2 flex-1">
                                  <span className="text-sm text-gray-700 group-hover:text-gray-900">
                                    {permissao.nome}
                                  </span>
                                  {permissao.descricao && (
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {permissao.descricao}
                                    </p>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>{permissoesSelecionadas.length}</strong> permissões selecionadas de <strong>{permissoesDisponiveis.length}</strong> disponíveis
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    {editingPerfil ? 'Salvar Alterações' : 'Criar Perfil'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  )
}

export default function PerfisPageWrapper() {
  return (
    <ProtectedRoute>
      <PerfisPage />
    </ProtectedRoute>
  )
}
